import React, { useEffect } from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  VStack, 
  HStack,
  Icon, 
  Image, 
  Avatar,
  Divider,
  useColorModeValue,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  IconButton,
  useBreakpointValue,
  CloseButton,
  Badge
} from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  MdDashboard, 
  MdGavel, 
  MdFolderSpecial, 
  MdDescription, 
  MdAssignment,
  MdAssessment,
  MdSettings,
  MdLogout,
  MdMenu,
  MdSearch
} from 'react-icons/md';
import { FaChevronRight, FaBalanceScale } from 'react-icons/fa';

const SidebarItem = ({ icon, label, to, isActive, badge }) => {
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, lg: false });
  
  const textColor = useColorModeValue('gray.700', 'whiteAlpha.900');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const activeBg = useColorModeValue('blue.50', 'blue.900');
  const activeTextColor = useColorModeValue('blue.600', 'blue.200');
  
  return (
    <Flex
      align="center"
      px="4"
      py={{ base: 3, lg: 2.5 }}
      mx={{ base: 0, lg: 2 }}
      borderRadius={{ base: "md", lg: "lg" }}
      role="group"
      cursor="pointer"
      bg={isActive ? activeBg : 'transparent'}
      color={isActive ? activeTextColor : textColor}
      _hover={{
        bg: isActive ? activeBg : hoverBg,
        color: isActive ? activeTextColor : textColor,
      }}
      transition="all 0.2s"
      onClick={() => {
        to && navigate(to);
        if (isMobile && window.onCloseMobileSidebar) {
          window.onCloseMobileSidebar();
        }
      }}
    >
      <Icon 
        as={icon} 
        fontSize={{ base: "24px", lg: "20px" }}
        mr="3"
        color={isActive ? activeTextColor : 'gray.500'}
      />
      <Text fontSize={{ base: "md", lg: "sm" }} fontWeight={isActive ? "semibold" : "medium"}>
        {label}
      </Text>
      {badge && (
        <Badge ml="2" colorScheme="red" fontSize="xs">
          {badge}
        </Badge>
      )}
      {isActive && (
        <Icon 
          as={FaChevronRight} 
          fontSize={{ base: "16px", lg: "14px" }}
          ml="auto" 
          color={activeTextColor}
        />
      )}
    </Flex>
  );
};

const JuridicoSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  
  const isMobile = useBreakpointValue({ base: true, lg: false });
  
  // Make onClose available globally for mobile
  useEffect(() => {
    if (isMobile) {
      window.onCloseMobileSidebar = onClose;
    }
    return () => {
      if (window.onCloseMobileSidebar) {
        delete window.onCloseMobileSidebar;
      }
    };
  }, [isMobile, onClose]);

  const textColor = useColorModeValue('gray.700', 'whiteAlpha.900');
  const bgSidebar = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const logoSrc = useColorModeValue(
    '/Assets/Logos/logo_mini.png',
    '/Assets/Logos/Logo-night.png'
  );

  const logout = () => {
    localStorage.removeItem('userInfo');
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const scrollbarThumbColor = useColorModeValue('gray.300', 'gray.600');

  const SidebarContent = () => (
    <Box 
      bg={bgSidebar} 
      w={{ base: "100%", lg: "250px" }}
      h="100vh" 
      py="5" 
      borderRight={{ base: "none", lg: "1px" }}
      borderColor={borderColor}
      position={{ base: "relative", lg: "fixed" }}
      left="0"
      top="0"
      overflowY="auto"
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          width: '6px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: scrollbarThumbColor,
          borderRadius: '24px',
        },
      }}
      shadow={{ base: "none", lg: "lg" }}
      transition="all 0.2s"
      zIndex="1000"
    >
      <Flex 
        direction="column" 
        h="full" 
        justify="space-between"
      >
        <Box>
                    <Flex 
            mb="3" 
            px="75" 
            alignItems="center" 
            justifyContent="space-between"
          >
            <Box maxW="100%">
              <Image 
                src={logoSrc}
                alt="Logo"
                maxH="75px"
                objectFit="contain"
              />
            </Box>
            {isMobile && (
              <CloseButton onClick={onClose} size="md" />
            )}
          </Flex>
          
          <HStack mb="6" px="5" spacing={3}>
            <Avatar 
              size="sm" 
              name={userInfo?.name || 'Jurídico'}
              src={userInfo?.avatar || ''}
              bg="blue.500"
              color="white"
            />
            <Box>
              <Text 
                fontSize="sm" 
                fontWeight="medium"
                color={textColor}
                noOfLines={1}
              >
                {userInfo?.name || 'Jurídico'}
              </Text>
              <Text 
                fontSize="xs" 
                color="gray.500"
                noOfLines={1}
              >
                Departamento Jurídico
              </Text>
            </Box>
          </HStack>
          
          <Divider mb="4" />
          
          <Text 
            px="5" 
            my="3" 
            color="gray.500" 
            fontSize="xs" 
            fontWeight="medium"
            letterSpacing="wider"
            textTransform="uppercase"
          >
            Menu Principal
          </Text>

          <VStack align="stretch" spacing={{ base: 2, lg: 1 }}>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdDashboard} 
                label="Dashboard" 
                to="/juridico/dashboard" 
                isActive={isActive('/juridico/dashboard')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdSearch} 
                label="Precatórios Disponíveis" 
                to="/juridico/precatorios-disponiveis" 
                isActive={isActive('/juridico/precatorios-disponiveis')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdAssignment} 
                label="Meus Precatórios" 
                to="/juridico/meus-precatorios" 
                isActive={isActive('/juridico/meus-precatorios')}
              />
            </Box>
          </VStack>
          
          <Divider my="4" />
          
          <Text 
            px="5" 
            my="3" 
            color="gray.500" 
            fontSize="xs" 
            fontWeight="medium"
            letterSpacing="wider"
            textTransform="uppercase"
          >
            Análise
          </Text>
          
          <VStack align="stretch" spacing={{ base: 2, lg: 1 }}>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdDescription} 
                label="Documentos" 
                to="/juridico/documentos" 
                isActive={isActive('/juridico/documentos')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={FaBalanceScale} 
                label="Pareceres" 
                to="/juridico/pareceres" 
                isActive={isActive('/juridico/pareceres')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdAssessment} 
                label="Relatórios" 
                to="/juridico/relatorios" 
                isActive={isActive('/juridico/relatorios')}
              />
            </Box>
          </VStack>
        </Box>
        
        <Box>
          <Divider mb="4" />
          <VStack align="stretch" spacing={{ base: 2, lg: 1 }}>
            <Box px={{ base: 2, lg: 0 }}>
              <SidebarItem 
                icon={MdSettings} 
                label="Configurações" 
                to="/juridico/configuracoes" 
                isActive={isActive('/juridico/configuracoes')}
              />
            </Box>
            <Box px={{ base: 2, lg: 0 }}>
              <Flex
                align="center"
                px="4"
                py={{ base: 3, lg: 2.5 }}
                mx={{ base: 0, lg: 2 }}
                borderRadius={{ base: "md", lg: "lg" }}
                cursor="pointer"
                color="red.500"
                _hover={{
                  bg: 'red.50',
                  color: 'red.600',
                }}
                transition="all 0.2s"
                onClick={logout}
              >
                <Icon as={MdLogout} fontSize={{ base: "24px", lg: "20px" }} mr="3" />
                <Text fontSize={{ base: "md", lg: "sm" }} fontWeight="medium">
                  Sair
                </Text>
              </Flex>
            </Box>
          </VStack>
        </Box>
      </Flex>
    </Box>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      {isMobile && (
        <IconButton
          aria-label="Open menu"
          icon={<MdMenu />}
          position="fixed"
          top="4"
          left="4"
          zIndex="999"
          onClick={onOpen}
          colorScheme="blue"
          variant="solid"
          size="md"
          shadow="md"
        />
      )}

      {/* Desktop Sidebar */}
      {!isMobile && <SidebarContent />}
      
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent>
            <SidebarContent />
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
};

export default JuridicoSidebar;